import React, { lazy, Suspense } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TitleProvider } from "./context/TitleContext";
import "./styles/moduleResponsive.css";

import AppVersionChecker from "./components/AppVersionChecker";
import Footer from "./pages/Footer";
import Login from "./components/login/Login";
import VerifyCode from "./components/login/VerifyCode";
import ResetPassword from "./components/login/ResetPassword";
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";
import ProtectedRoute from "./components/ProtectedRoute";

const Layout = lazy(() => import("./pages/Layout"));
const Home = lazy(() => import("./pages/Home/Home"));
const ExportOrders = lazy(() => import("./components/exportOrders/ExportOrders"));
const Favourites = lazy(() => import("./components/favourites/Favourites"));
const Branches = lazy(() => import("./components/branches/Branches"));
const EditBranch = lazy(() => import("./components/branches/EditBranch"));
const AddBranch = lazy(() => import("./components/branches/AddBranch"));
const ViewBranch = lazy(() => import("./components/branches/ViewBranch"));
const Vendors = lazy(() => import("./components/vendors/Vendors"));
const AddVendor = lazy(() => import("./components/vendors/AddVendor"));
const EditSupplier = lazy(() => import("./components/vendors/EditSupplier"));
const Clients = lazy(() => import("./components/clients/Clients"));
const AddClient = lazy(() => import("./components/clients/AddClient"));
const EditClient = lazy(() => import("./components/clients/EditClient"));
const ShowClient = lazy(() => import("./components/clients/ShowClient"));
const LeadsCustomer = lazy(() => import("./components/Connectwithcustomers/LeadsCustomers/LeadsCustomer"));
const AddLead = lazy(() => import("./components/Connectwithcustomers/LeadsCustomers/AddLead"));
const EditLead = lazy(() => import("./components/Connectwithcustomers/LeadsCustomers/EditLead"));
const ShowLead = lazy(() => import("./components/Connectwithcustomers/LeadsCustomers/ShowLead"));
const LeadActivities = lazy(() => import("./components/Connectwithcustomers/LeadsCustomers/LeadActivities"));
const TargetCustomer = lazy(() => import("./components/Connectwithcustomers/TargetCustomer/TargetCustomer"));
const ShowTargetCustomer = lazy(() => import("./components/Connectwithcustomers/TargetCustomer/ShowTargetCustomer"));
const Ratings = lazy(() => import("./components/ratings/Ratings"));
const BusinessSector = lazy(() => import("./components/businessSector/BusinessSector"));
const AddBusinessSector = lazy(() => import("./components/businessSector/AddBusinessSector"));
const EditBusinessSector = lazy(() => import("./components/businessSector/EditBusinessSector"));
const ShowBusinessSector = lazy(() => import("./components/businessSector/ShowBusinessSector"));
const BusinessSectorUsers = lazy(() => import("./components/businessSector/BusinessSectorUsers"));
const AddBusinessSectorUser = lazy(() => import("./components/businessSector/AddBusinessSectorUser"));
const ShowBusinessSectorUser = lazy(() => import("./components/businessSector/ShowBusinessSectorUser"));
const EditBusinessSectorUser = lazy(() => import("./components/businessSector/EditBusinessSectorUser"));
const OfficialRecievingDocs = lazy(() => import("./components/businessSector/OfficialRecievingDocs"));
const AddBusinessSectorResponsible = lazy(() => import("./components/businessSector/AddBusinessSectorResponsible"));
const BusinessSectorContracts = lazy(() => import("./components/businessSector/BusinessSectorContracts"));
const AddBusinessSectorContract = lazy(() => import("./components/businessSector/AddBusinessSectorContract"));
const AddElectronicContract = lazy(() => import("./components/businessSector/AddElectronicContract"));
const ElectronicContractSetup = lazy(() => import("./components/businessSector/ElectronicContractSetup"));
const ElectronicContractPreviewPage = lazy(() =>
  import("./components/businessSector/ElectronicContractPreviewPage")
);
const EditBusinessSectorContract = lazy(() => import("./components/businessSector/EditBusinessSectorContract"));
const CreditLimit = lazy(() => import("./components/businessSector/CreditLimit"));
const BusinessDocs = lazy(() => import("./components/businessSector/BusinessDocs"));
const AddBusinessSectorDocument = lazy(() => import("./components/businessSector/AddBusinessSectorDocument"));
const TripwayPartnersList = lazy(() => import("./components/tripwayPartners/TripwayPartnersList"));
const AddTripwayPartner = lazy(() => import("./components/tripwayPartners/AddTripwayPartner"));
const EditTripwayPartner = lazy(() => import("./components/tripwayPartners/EditTripwayPartner"));
const ShowTripwayPartner = lazy(() => import("./components/tripwayPartners/ShowTripwayPartner"));
const UsersWinchPartners = lazy(() => import("./components/tripwayPartners/UsersWinchPartners"));
const AddWinchPartnerUser = lazy(() => import("./components/tripwayPartners/AddWinchPartnerUser"));
const EditWinchPartnerUser = lazy(() => import("./components/tripwayPartners/EditWinchPartnerUser"));
const ViewWinchPartnerUser = lazy(() => import("./components/tripwayPartners/ViewWinchPartnerUser"));
const UserPermissions = lazy(() => import("./components/tripwayPartners/UserPermissions"));
const AddRole = lazy(() => import("./components/tripwayPartners/AddRole"));
const EditRole = lazy(() => import("./components/tripwayPartners/EditRole"));
const ClientsPartnersWinch = lazy(() => import("./components/tripwayPartners/ClientsPartnersWinch"));
const PartnersContracts = lazy(() => import("./components/tripwayPartners/PartnersContracts"));
const PartnersDocs = lazy(() => import("./components/tripwayPartners/PartnersDocs"));
const AddWinchPartnerDocument = lazy(() => import("./components/tripwayPartners/AddWinchPartnerDocument"));
const AddWinchPartnerContract = lazy(() => import("./components/tripwayPartners/AddWinchPartnerContract"));
const DocsCarriers = lazy(() => import("./components/tripwayPartners/DocsCarriers"));
const ExceededCommissionAccounts = lazy(() => import("./components/serviceProviders/ExceededCommissionAccounts"));
const Drivers = lazy(() => import("./components/serviceProviders/Drivers"));
const AddDriver = lazy(() => import("./components/serviceProviders/AddDriver"));
const ShowDriver = lazy(() => import("./components/serviceProviders/ShowDriver"));
const EditDriver = lazy(() => import("./components/serviceProviders/EditDriver"));
const VehiclesPlural = lazy(() => import("./components/serviceProviders/VehiclesPlural"));
const AddVehicle = lazy(() => import("./components/serviceProviders/AddVehicle"));
const EditVehicle = lazy(() => import("./components/serviceProviders/EditVehicle"));
const ViewVehicle = lazy(() => import("./components/serviceProviders/ViewVehicle"));
const AddProvider = lazy(() => import("./components/serviceProviders/AddProvider"));
const BankAccounts = lazy(() => import("./components/bankAccounts/BankAccounts"));
const Banks = lazy(() => import("./components/bankAccounts/Banks"));
const AddBank = lazy(() => import("./components/bankAccounts/AddBank"));
const EditBank = lazy(() => import("./components/bankAccounts/EditBank"));
const WithdrawalAndDeposit = lazy(() => import("./components/withdrawalAndDeposit/WithdrawalAndDeposit"));
const TripRequests = lazy(() => import("./components/tripRequests/TripRequests"));
const ViewTripRequest = lazy(() => import("./components/tripRequests/ViewTripRequest"));
const ReceiveTripDocuments = lazy(() => import("./components/tripRequests/ReceiveTripDocuments"));
const EditTripRequest = lazy(() => import("./components/tripRequests/EditTripRequest"));
const InternalRequests = lazy(() => import("./components/tripRequests/InternalRequests"));
const CompensationApprovals = lazy(() => import("./components/tripRequests/CompensationApprovals"));
const OrderIssueReports = lazy(() => import("./components/tripRequests/OrderIssueReports"));
const OrderDocumentReception = lazy(() => import("./components/tripRequests/OrderDocumentReception"));
const DocumentsAndFiles = lazy(() => import("./components/tripRequests/DocumentsAndFiles"));
const OrderSummary = lazy(() => import("./components/tripRequests/OrderSummary"));
const EditOrderDetails = lazy(() => import("./components/tripRequests/EditOrderDetails"));
const EditOrderPrices = lazy(() => import("./components/tripRequests/EditOrderPrices"));
const EditShipmentNumbers = lazy(() => import("./components/tripRequests/EditShipmentNumbers"));
const TripReports = lazy(() => import("./components/tripRequests/TripReports"));
const DuplicateShipmentNumbers = lazy(() => import("./components/tripRequests/DuplicateShipmentNumbers"));
const LateEntries = lazy(() => import("./components/tripRequests/LateEntries"));
const RentalRequests = lazy(() => import("./components/mainRentals/RentalRequests"));
const RentalProjectsMain = lazy(() => import("./components/mainRentals/RentalProjectsMain"));
const AddRentalProjectsMain = lazy(() => import("./components/mainRentals/AddRentalProjectsMain"));
const RentalContracts = lazy(() => import("./components/mainRentals/RentalContracts"));
const RentalRequestSummary = lazy(() => import("./components/mainRentals/RentalRequestSummary"));
const RentalReports = lazy(() => import("./components/mainRentals/RentalReports"));
const AddRentalContract = lazy(() => import("./components/mainRentals/AddRentalContract"));
const TransportDocuments = lazy(() => import("./components/transportDocuments/TransportDocuments"));
const AddTransportDocument = lazy(() => import("./components/transportDocuments/AddTransportDocument"));
const EditTransportDocument = lazy(() => import("./components/transportDocuments/EditTransportDocument"));
const ViewTransportDocument = lazy(() => import("./components/transportDocuments/ViewTransportDocument"));
const ManualGroupOrder = lazy(() => import("./components/tripRequests/ManualGroupOrder/Manual-Group-Order"));
const AddManualGroupOrder = lazy(() => import("./components/tripRequests/ManualGroupOrder/AddManualGroupOrder"));
const ViewManualGroupOrder = lazy(() => import("./components/tripRequests/ManualGroupOrder/ViewManualGroupOrder"));
const PriceOfferRequests = lazy(() => import("./components/priceOffers/PriceOfferRequests"));
const CreatePriceOfferRequests = lazy(() => import("./components/priceOffers/CreatePriceOfferRequests"));
const ClientOffers = lazy(() => import("./components/priceOffers/ClientOffer/ClientOffers"));
const AddQuotationRequestCustomer = lazy(() => import("./components/priceOffers/ClientOffer/AddQuotationRequestCustomer"));
const EditQuotationRequestCustomer = lazy(() => import("./components/priceOffers/ClientOffer/EditQuotationRequestCustomer"));
const ViewQuotationRequestCustomer = lazy(() => import("./components/priceOffers/ClientOffer/ViewQuotationRequestCustomer"));
const QuotationsRequests = lazy(() => import("./components/priceOffers/QuotationsRequests/QuotationsRequests"));
const AddQuotationRequest = lazy(() => import("./components/priceOffers/QuotationsRequests/AddQuotationRequest"));
const ViewQuotationRequest = lazy(() => import("./components/priceOffers/QuotationsRequests/ViewQuotationRequest"));
const QuotationsRequestsSuppliers = lazy(() => import("./components/priceOffers/QuotationsRequestsSuppliers/QuotationsRequestsSuppliers"));
const AddQuotationRequestSupplier = lazy(() => import("./components/priceOffers/QuotationsRequestsSuppliers/AddQuotationRequestSupplier"));
const EditQuotationRequestSupplier = lazy(() => import("./components/priceOffers/QuotationsRequestsSuppliers/EditQuotationRequestSupplier"));
const ViewQuotationRequestSupplier = lazy(() => import("./components/priceOffers/QuotationsRequestsSuppliers/ViewQuotationRequestSupplier"));
const ProviderOffers = lazy(() => import("./components/priceOffers/ProviderOffers"));
const Profits = lazy(() => import("./components/reportsAndStatistics/Profits"));
const MostRequestedServices = lazy(() => import("./components/reportsAndStatistics/MostRequestedServices"));
const ActiveAccounts = lazy(() => import("./components/reportsAndStatistics/ActiveAccounts"));
const Taxes = lazy(() => import("./components/reportsAndStatistics/Taxes"));
const TaxCalculation = lazy(() => import("./components/reportsAndStatistics/TaxCalculation"));
const NegativeAccounts = lazy(() => import("./components/reportsAndStatistics/NegativeAccounts"));
const LowestCostPrice = lazy(() => import("./components/reportsAndStatistics/LowestCostPrice"));
const DocumentReceptionReports = lazy(() => import("./components/reportsAndStatistics/DocumentReceptionReports"));
const AccountManagerRequestReports = lazy(() => import("./components/reportsAndStatistics/AccountManagerRequestReports"));
const OperatorTripReports = lazy(() => import("./components/reportsAndStatistics/OperatorTripReports"));
const TripRequestReport = lazy(() => import("./components/reportsAndStatistics/TripRequestReport"));
const PurchaseRequests = lazy(() => import("./components/purchaseRequests/PurchaseRequests/PurchaseRequests"));
const InitialPurchaseRequests = lazy(() => import("./components/purchaseRequests/InitialPurchaseRequests"));
const Maintenance = lazy(() => import("./components/fleetVehiclesManagement/Maintenance"));
const AddMaintenanceRequest = lazy(() => import("./components/fleetVehiclesManagement/AddMaintenanceRequest"));
const Fuel = lazy(() => import("./components/fleetVehiclesManagement/Fuel"));
const AddFuelRequest = lazy(() => import("./components/fleetVehiclesManagement/AddFuelRequest"));
const EditFuelRequest = lazy(() => import("./components/fleetVehiclesManagement/EditFuelRequest"));
const ViewFuel = lazy(() => import("./components/fleetVehiclesManagement/ViewFuel"));
const VehicleStatement = lazy(() => import("./components/fleetVehiclesManagement/vehicleStatement"));
const AddEntry = lazy(() => import("./components/fleetVehiclesManagement/AddEntry"));
const EditEntry = lazy(() => import("./components/fleetVehiclesManagement/EditEntry"));
const ViewEntry = lazy(() => import("./components/fleetVehiclesManagement/ViewEntry"));
const SalesInvoices = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/SalesInvoices"));
const CreditNotes = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/CreditNotes"));
const ManualCreditNote = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/manual/ManualCreditNote"));
const ShowCreditNote = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/ShowCreditNote"));
const EditCreditNoteItems = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/manual/EditCreditNoteItems"));
const PurchaseInvoices = lazy(() => import("./components/invoicesAndDocuments/purchase/PurchaseInvoices"));
const DebitNotes = lazy(() => import("./components/invoicesAndDocuments/purchase/DebitNotes"));
const ManualDebitNote = lazy(() => import("./components/invoicesAndDocuments/purchase/manual/ManualDebitNote"));
const ShowDebitNote = lazy(() => import("./components/invoicesAndDocuments/purchase/ShowDebitNote"));
const EditDebitNoteItems = lazy(() => import("./components/invoicesAndDocuments/purchase/manual/EditDebitNoteItems"));
const CreatePurchaseInvoices = lazy(() => import("./components/invoicesAndDocuments/purchase/manual/CreatePurchaseInvoices"));
const CreateCollectedTravelPurchaseInvoice = lazy(() => import("./components/invoicesAndDocuments/purchase/collected/CreateCollectedTravelPurchaseInvoice"));
const CreateCollectedRentalPurchaseInvoice = lazy(() => import("./components/invoicesAndDocuments/purchase/collected/CreateCollectedRentalPurchaseInvoice"));
const Vouchers = lazy(() => import("./components/invoicesAndDocuments/Vouchers"));
const CustomerVouchers = lazy(() => import("./components/invoicesAndDocuments/CustomerVouchers"));
const InternalSales = lazy(() => import("./components/internalInvoices/InternalSales"));
const InternalPurchases = lazy(() => import("./components/internalInvoices/InternalPurchases"));
const InternalVouchers = lazy(() => import("./components/internalInvoices/InternalVouchers"));
const Archive = lazy(() => import("./components/archive/Archive"));
const Commissions = lazy(() => import("./components/commissions/Commissions"));
const CommissionReports = lazy(() => import("./components/commissions/CommissionReports"));
const Register = lazy(() => import("./components/register/Register"));
const Profile = lazy(() => import("./components/Profile/Profile"));
const Service_categories = lazy(() => import("./components/Settings/service_categories/service_categories"));
const AddServiceCategories = lazy(() => import("./components/Settings/service_categories/add_service"));
const EditServiceCategories = lazy(() => import("./components/Settings/service_categories/edit_service"));
const Compensation_categories = lazy(() => import("./components/Settings/compenastionCategories/compensation_categories"));
const Addcompensation_categories = lazy(() => import("./components/Settings/compenastionCategories/add_compensation_categories"));
const Editcompensation_categories = lazy(() => import("./components/Settings/compenastionCategories/edit_compensation_categories"));
const CompanyManagements = lazy(() => import("./components/Settings/company_managements/CompanyManagements"));
const AddManagement = lazy(() => import("./components/Settings/company_managements/AddManagement"));
const EditManagement = lazy(() => import("./components/Settings/company_managements/EditManagement"));
const FleetManagementTypes = lazy(() => import("./components/Settings/fleetManagementTypes/FleetManagementTypes"));
const ShowCountries = lazy(() => import("./components/Countries&Cities/countries/ShowCountries"));
const EditCountries = lazy(() => import("./components/Countries&Cities/countries/EditCountries"));
const AddCountries = lazy(() => import("./components/Countries&Cities/countries/AddCountries"));
const AddRegion = lazy(() => import("./components/Countries&Cities/Region/AddRegion"));
const EditRegion = lazy(() => import("./components/Countries&Cities/Region/EditRegion"));
const ShowRegion = lazy(() => import("./components/Countries&Cities/Region/ShowRegion"));
const ShowCities = lazy(() => import("./components/Countries&Cities/cities/ShowCities"));
const EditCities = lazy(() => import("./components/Countries&Cities/cities/EditCities"));
const AddCities = lazy(() => import("./components/Countries&Cities/cities/AddCities"));
const ShowWinchCities = lazy(() => import("./components/Countries&Cities/cities/ShowWinchCities"));
const GoogleMapLinks = lazy(() => import("./components/Settings/googleMapLinks/googleMapLinks"));
const ShowPositions = lazy(() => import("./components/Settings/positions/ShowPositions"));
const AddPositions = lazy(() => import("./components/Settings/positions/AddPositions"));
const EditPositions = lazy(() => import("./components/Settings/positions/EditPositions"));
const ShowOffices = lazy(() => import("./components/Settings/Offices/ShowOffices"));
const AddOffices = lazy(() => import("./components/Settings/Offices/AddOffices"));
const EditOffices = lazy(() => import("./components/Settings/Offices/EditOffices"));
const AddFleetRequest = lazy(() => import("./components/Settings/fleetManagementTypes/AddFleetRequest"));
const EditFleetRequest = lazy(() => import("./components/Settings/fleetManagementTypes/EditFleetRequest"));
const FileArchiveTypes = lazy(() => import("./components/Settings/fileArchiveTypes/FileArchiveTypes"));
const AddFileArchiveTypes = lazy(() => import("./components/Settings/fileArchiveTypes/AddFileArchiveTypes"));
const EditFileArchiveTypes = lazy(() => import("./components/Settings/fileArchiveTypes/EditFileArchiveTypes"));
const VehicleCompanies = lazy(() => import("./components/Settings/vehicleCompanies/VehicleCompanies"));
const AddVehicleCompanies = lazy(() => import("./components/Settings/vehicleCompanies/AddVehicleCompanies"));
const EditVehicleCompanies = lazy(() => import("./components/Settings/vehicleCompanies/EditVehicleCompanies"));
const OrderIssueTypes = lazy(() => import("./components/Settings/orderIssueTypes/OrderIssueTypes"));
const AddOrderIssueTypes = lazy(() => import("./components/Settings/orderIssueTypes/AddOrderIssueTypes"));
const EditOrderIssueTypes = lazy(() => import("./components/Settings/orderIssueTypes/EditOrderIssueTypes"));
const ServicesTypes = lazy(() => import("./components/Settings/ServicesTypes/ServicesTypes"));
const AddServicesTypes = lazy(() => import("./components/Settings/ServicesTypes/AddServicesTypes"));
const EditServicesTypes = lazy(() => import("./components/Settings/ServicesTypes/EditServicesTypes"));
const ShowServicesTypes = lazy(() => import("./components/Settings/ServicesTypes/ShowServicesTypes"));
const QuotationFeatures = lazy(() => import("./components/Settings/QuotationFeatures/QuotationFeatures"));
const AddQuotationFeatures = lazy(() => import("./components/Settings/QuotationFeatures/AddQuotationFeatures"));
const EditQuotationFeatures = lazy(() => import("./components/Settings/QuotationFeatures/EditQuotationFeatures"));
const LoadTypes = lazy(() => import("./components/Settings/LoadTypes/LoadTypes"));
const AddLoadTypes = lazy(() => import("./components/Settings/LoadTypes/AddLoadTypes"));
const EditLoadTypes = lazy(() => import("./components/Settings/LoadTypes/EditLoadTypes"));
const GoodTypes = lazy(() => import("./components/Settings/GoodTypes/GoodTypes"));
const AddGoodTypes = lazy(() => import("./components/Settings/GoodTypes/AddGoodTypes"));
const EditGoodTypes = lazy(() => import("./components/Settings/GoodTypes/EditGoodTypes"));
const CarDepartments = lazy(() => import("./components/CarDepartments/CarDepartments"));
const AddCarDepartments = lazy(() => import("./components/CarDepartments/AddCarDepartments"));
const EditCarDepartments = lazy(() => import("./components/CarDepartments/EditCarDepartments"));
const MainSettings = lazy(() => import("./components/Settings/MainSettings/MainSettings"));
const AddJob = lazy(() => import("./components/Settings/Jobs/AddJob"));
const EditJob = lazy(() => import("./components/Settings/Jobs/EditJob"));
const OfficeDocumentType = lazy(() => import("./components/Settings/OfficeDocumentType/OfficeDocumentType"));
const AddOfficeDocType = lazy(() => import("./components/Settings/OfficeDocumentType/AddOfficeDocType"));
const EditOfficeDocType = lazy(() => import("./components/Settings/OfficeDocumentType/EditOfficeDocType"));
const DocumentType = lazy(() => import("./components/Settings/DocumentType/DocumentType"));
const AddDocumentType = lazy(() => import("./components/Settings/DocumentType/AddDocumentType"));
const EditDocumentType = lazy(() => import("./components/Settings/DocumentType/EditDocumentType"));
const AttachmentsType = lazy(() => import("./components/Settings/AttachmentTypes/AttachmentsType"));
const AddAttachments = lazy(() => import("./components/Settings/AttachmentTypes/add_attachment"));
const EditAttachments = lazy(() => import("./components/Settings/AttachmentTypes/EditAttachments"));
const JobCompanyManagements = lazy(() => import("./components/Settings/Jobs/JobCompanyManagements"));
const SalesAccounts = lazy(() => import("./components/Settings/salesAccounts/SalesAccounts"));
const AddSalesAccount = lazy(() => import("./components/Settings/salesAccounts/AddSalesAccount"));
const EditSalesAccount = lazy(() => import("./components/Settings/salesAccounts/EditSalesAccount"));
const SalesReturnsAccounts = lazy(() => import("./components/Settings/SalesReturnsAccounts/SalesReturnsAccounts"));
const AddSalesReturnsAccounts = lazy(() => import("./components/Settings/SalesReturnsAccounts/AddSalesReturnsAccounts"));
const EditSalesReturnsAccounts = lazy(() => import("./components/Settings/SalesReturnsAccounts/EditSalesReturnsAccounts"));
const RelatedPartyAccounts = lazy(() => import("./components/Settings/RelatedPartyAccounts/RelatedPartyAccounts"));
const AddRelatedPartyAccounts = lazy(() => import("./components/Settings/RelatedPartyAccounts/AddRelatedPartyAccounts"));
const EditRelatedPartyAccounts = lazy(() => import("./components/Settings/RelatedPartyAccounts/EditRelatedPartyAccounts"));
const PurchasesAccounts = lazy(() => import("./components/Settings/PurchasesAccounts/PurchasesAccounts"));
const AddPurchasesAccounts = lazy(() => import("./components/Settings/PurchasesAccounts/AddPurchasesAccounts"));
const EditPurchasesAccounts = lazy(() => import("./components/Settings/PurchasesAccounts/EditPurchasesAccounts"));
const PurchaseReturnsAccounts = lazy(() => import("./components/Settings/PurchaseReturnsAccounts/PurchaseReturnsAccounts"));
const AddPurchaseReturnsAccounts = lazy(() => import("./components/Settings/PurchaseReturnsAccounts/AddPurchaseReturnsAccounts"));
const EditPurchaseReturnsAccounts = lazy(() => import("./components/Settings/PurchaseReturnsAccounts/EditPurchaseReturnsAccounts"));
const Employees = lazy(() => import("./components/employees/Employees"));
const AddEmployees = lazy(() => import("./components/employees/AddEmployees"));
const ShowEmployee = lazy(() => import("./components/employees/ShowEmployee"));
const EditEmployee = lazy(() => import("./components/employees/EditEmployee"));
const TeamworkMovements = lazy(() => import("./components/employees/TeamworkMovements"));
const TeamworkMovementsActivities = lazy(() => import("./components/employees/TeamworkMovementsActivities"));
const Faqs = lazy(() => import("./components/faqs/FAQs"));
const AddFAQs = lazy(() => import("./components/faqs/AddFAQs"));
const EditFAQs = lazy(() => import("./components/faqs/EditFAQs"));
const FurnitureQuestions = lazy(() => import("./components/Settings/FurnitureQuestions/FurnitureQuestions"));
const AddFurnitureQuestions = lazy(() => import("./components/Settings/FurnitureQuestions/AddFurnitureQuestions"));
const EditFurnitureQuestions = lazy(() => import("./components/Settings/FurnitureQuestions/EditFurnitureQuestions"));
const CategoryRushHours = lazy(() => import("./components/Settings/CategoryRushHours/CategoryRushHours"));
const EditCategoryRushHours = lazy(() => import("./components/Settings/CategoryRushHours/EditCategoryRushHours"));
const ChartOfAccounts = lazy(() => import("./components/accounting/ChartOfAccounts/ChartOfAccounts"));
const AddChartOfAccounts = lazy(() => import("./components/accounting/ChartOfAccounts/AddChartOfAccounts"));
const EditChartOfAccounts = lazy(() => import("./components/accounting/ChartOfAccounts/EditChartOfAccounts"));
const ShowChartOfAccounts = lazy(() => import("./components/accounting/ChartOfAccounts/ShowChartOfAccounts"));
const EasyEntries = lazy(() => import("./components/accounting/EasyEntries/EasyEntries"));
const AddEasyEntries = lazy(() => import("./components/accounting/EasyEntries/AddEasyEntries"));
const EditEasyEntries = lazy(() => import("./components/accounting/EasyEntries/EditEasyEntries"));
const ManualEntries = lazy(() => import("./components/accounting/ManualEntries/ManualEntries"));
const AddManualEntry = lazy(() => import("./components/accounting/ManualEntries/AddManualEntry"));
const ViewManualEntry = lazy(() => import("./components/accounting/ManualEntries/ViewManualEntry"));
const EditManualEntry = lazy(() => import("./components/accounting/ManualEntries/EditManualEntry"));
const CustomerBalances = lazy(() => import("./components/accounting/Reports/CustomerBalances"));
const AccountStatment = lazy(() => import("./components/accounting/Reports/AccountStatment"));
const Generalledger = lazy(() => import("./components/accounting/Reports/Generalledger"));
const JournalBook = lazy(() => import("./components/accounting/Reports/JournalBook"));
const TrialBalance = lazy(() => import("./components/accounting/Reports/trialBalance"));
const CustomerDue = lazy(() => import("./components/accounting/Reports/customerDue"));
const IncomeStatement = lazy(() => import("./components/accounting/Reports/IncomeStatement"));
const BalanceSheet = lazy(() => import("./components/accounting/Reports/balancesheet"));
const TaxInvoices = lazy(() => import("./components/accounting/Reports/taxinvoices"));
const CustomerProfit = lazy(() => import("./components/accounting/Reports/customerProfit"));
const Roles = lazy(() => import("./components/roles/Roles"));
const AddUserRole = lazy(() => import("./components/roles/AddUserRole"));
const EditUserRole = lazy(() => import("./components/roles/EditUserRole"));
const ShowUserRole = lazy(() => import("./components/roles/ShowUserRole"));
const Permissions = lazy(() => import("./components/permissions/Permissions"));
const AddPermission = lazy(() => import("./components/permissions/AddPermission"));
const EditPermission = lazy(() => import("./components/permissions/EditPermission"));
const MaintenanceTable = lazy(() => import("./components/LeasingRequest/Maintenance/MaintenanceTable"));
const AddLeasingMaintenance = lazy(() => import("./components/LeasingRequest/Maintenance/AddLeasingMaintenance"));
const EditLeasingMaintenance = lazy(() => import("./components/LeasingRequest/Maintenance/EditLeasingMaintenance"));
const ViewLeasingMaintenance = lazy(() => import("./components/LeasingRequest/Maintenance/ViewLeasingMaintenance"));
const EntriesTable = lazy(() => import("./components/LeasingRequest/Entries/EntriesTable"));
const AddLeasingEntry = lazy(() => import("./components/LeasingRequest/Entries/AddLeasingEntry"));
const EditLeasingEntry = lazy(() => import("./components/LeasingRequest/Entries/EditLeasingEntry"));
const ViewLeasingEntry = lazy(() => import("./components/LeasingRequest/Entries/ViewLeasingEntry"));
const FuelTable = lazy(() => import("./components/LeasingRequest/Fuel/FuelTable"));
const AddLeasingFuel = lazy(() => import("./components/LeasingRequest/Fuel/AddLeasingFuel"));
const EditLeasingFuel = lazy(() => import("./components/LeasingRequest/Fuel/EditLeasingFuel"));
const ViewLeasingFuel = lazy(() => import("./components/LeasingRequest/Fuel/ViewLeasingFuel"));
const RentalOrdersMain = lazy(() => import("./components/mainRentals/RentalOrdersMain/RentalOrdersMain"));
const AddRentalOrdersMain = lazy(() => import("./components/mainRentals/RentalOrdersMain/AddRentalOrdersMain"));
const RentalContractsMain = lazy(() => import("./components/mainRentals/RentalContractsMain/RentalContractsMain"));
const AddRentalContractsMain = lazy(() => import("./components/mainRentals/RentalContractsMain/AddRentalContractsMain"));
const ShowRentalContractsMain = lazy(() => import("./components/mainRentals/RentalContractsMain/ShowRentalContractsMain"));
const EstablishmentDocuments = lazy(() => import("./components/Documents/EstablishmentDocuments/EstablishmentDocuments"));
const AddEstablishmentDocuments = lazy(() => import("./components/Documents/EstablishmentDocuments/AddEstablishmentDocuments"));
const ShowEstablishmentDocuments = lazy(() => import("./components/Documents/EstablishmentDocuments/ShowEstablishmentDocuments"));
const Bonds = lazy(() => import("./components/invoicesAndDocuments/Bonds/Bonds"));
const AddBond = lazy(() => import("./components/invoicesAndDocuments/Bonds/AddBond"));
const EditBond = lazy(() => import("./components/invoicesAndDocuments/Bonds/EditBond"));
const ShowBond = lazy(() => import("./components/invoicesAndDocuments/Bonds/ShowBond"));
const EmployeeDocument = lazy(() => import("./components/Documents/EmployeeDocument/EmployeeDocument"));
const AddEmployeeDocument = lazy(() => import("./components/Documents/EmployeeDocument/AddEmployeeDocument"));
const ShowEmployeeDocument = lazy(() => import("./components/Documents/EmployeeDocument/ShowEmployeeDocument"));
const VehicleDocument = lazy(() => import("./components/Documents/VehicleDocument/VehicleDocument"));
const AddVehicleDocument = lazy(() => import("./components/Documents/VehicleDocument/AddVehicleDocument"));
const ShowVehicleDocument = lazy(() => import("./components/Documents/VehicleDocument/ShowVehicleDocument"));
const AddDriverDocument = lazy(() => import("./components/Documents/DriverDocument/AddDriverDocument"));
const ShowDriverDocument = lazy(() => import("./components/Documents/DriverDocument/ShowDriverDocument"));
const DriverDocument = lazy(() => import("./components/Documents/DriverDocument/DriverDocument"));
const AddFleetMandates = lazy(() => import("./components/Documents/FleetMandates/AddFleetMandates"));
const FleetMandates = lazy(() => import("./components/Documents/FleetMandates/FleetMandates"));
const AddCostCenterAccount = lazy(() => import("./components/accounting/cost_center_accounts/AddCostCenterAccount"));
const CostCenterAccounts = lazy(() => import("./components/accounting/cost_center_accounts/CostCenterAccounts"));
const CreateSalesInvoices = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/manual/CreateSalesInvoices"));
const CreateCollectedTravelSalesInvoice = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/collected/CreateCollectedTravelSalesInvoice"));
const CreateCollectedRentalSalesInvoice = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/collected/CreateCollectedRentalSalesInvoice"));
const ShowSalesInvoice = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/ShowSalesInvoice"));
const EditSalesInvoiceDetails = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/EditSalesInvoiceDetails"));
const EditSalesInvoiceItems = lazy(() => import("./components/invoicesAndDocuments/SalesInvoices/manual/EditSalesInvoiceItems"));
const ShowPurchaseInvoice = lazy(() => import("./components/invoicesAndDocuments/purchase/ShowPurchaseInvoice"));
const EditPurchaseInvoiceDetails = lazy(() => import("./components/invoicesAndDocuments/purchase/EditPurchaseInvoiceDetails"));
const EditPurchaseInvoiceItems = lazy(() => import("./components/invoicesAndDocuments/purchase/manual/EditPurchaseInvoiceItems"));

function App() {
  return (
    <>
      <TitleProvider>
        <AppVersionChecker />
        <ToastContainer position="top-right" 
        autoClose={3000} newestOnTop />
        <Router>
          <Suspense fallback={<div className="d-flex justify-content-center align-items-center min-vh-100" aria-busy="true" aria-label="Loading"><div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading...</span></div></div>}>
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Home />} />

              <Route
                path="users_winch_partners"
                element={<UsersWinchPartners />}
              />

              <Route
                path="clients_partners_winch"
                element={<ClientsPartnersWinch />}
              />
              <Route
                path="partners_contracts"
                element={<PartnersContracts />}
              />
              <Route path="partners_docs" element={<PartnersDocs />} />
              <Route path="winch-partner-documents/add" element={<AddWinchPartnerDocument />} />
              <Route path="winch-partner-contracts/add" element={<AddWinchPartnerContract />} />
              <Route path="docs_carriers" element={<DocsCarriers />} />
              
              <Route path="drivers/add_provider" element={<AddProvider />} />
              <Route path="vehicles_plural" element={<VehiclesPlural />} />
              <Route path="vehicles_plural/add_vehicle" element={<AddVehicle />} />
              <Route path="vehicles_plural/edit/:id" element={<EditVehicle />} />
              <Route path="vehicles_plural/view/:id" element={<ViewVehicle />} />

              <Route path="bank_accounts" element={<BankAccounts />} />
              <Route path="banks" element={<Banks />} />
              <Route path="add_bank" element={<AddBank />} />
              <Route path="edit_bank/:id" element={<EditBank />} />
              <Route
                path="withdrawal_deposit"
                element={<WithdrawalAndDeposit />}
              />
              <Route path="trip_requests" element={<TripRequests />} />
              <Route path="trip_requests/view/:id" element={<ViewTripRequest />} />
              <Route
                path="trip_requests/receive-documents/:id"
                element={<ReceiveTripDocuments />}
              />
              <Route path="trip_requests/edit/:id" element={<EditTripRequest />} />
              <Route path="internal_requests" element={<InternalRequests />} />
              <Route
                path="compensation_approvals"
                element={<CompensationApprovals />}
              />
              <Route
                path="order_issue_reports"
                element={<OrderIssueReports />}
              />
              <Route
                path="order_document_reception"
                element={<OrderDocumentReception />}
              />
              <Route path="documents_files" element={<DocumentsAndFiles />} />
              <Route path="order_summary" element={<OrderSummary />} />
              <Route path="edit_order_details" element={<EditOrderDetails />} />
              <Route path="edit_order_prices" element={<EditOrderPrices />} />
              <Route
                path="edit_shipment_numbers"
                element={<EditShipmentNumbers />}
              />
              <Route path="trip_reports" element={<TripReports />} />
              <Route
                path="duplicate_shipment_numbers"
                element={<DuplicateShipmentNumbers />}
              />
              <Route path="late_entries" element={<LateEntries />} />
              <Route path="rental_requests" element={<RentalRequests />} />
              <Route path="rental_projects_main" element={<RentalProjectsMain />} />
              <Route path="rental_projects_main/add" element={<AddRentalProjectsMain />} />
              {/* <Route path="rental_orders_main" element={<RentalOrdersMain />} /> */}
              
              <Route path="rental_orders_main/add" element={<AddRentalOrdersMain />} />
              <Route path="rental_orders_main" element={<RentalOrdersMain />} />
              <Route path="rental_contracts_main/add" element={<AddRentalContractsMain />} />
              <Route path="rental_contracts_main/show/:id" element={<ShowRentalContractsMain />} />
              <Route path="rental_contracts_main" element={<RentalContractsMain />} />
              <Route path="rental_contracts" element={<RentalContracts />} />
              <Route
                path="rental_request_summary"
                element={<RentalRequestSummary />}
              />
              <Route path="rental_reports" element={<RentalReports />} />
              <Route
                path="rental_contracts/add_rental_contract"
                element={<AddRentalContract />}
              />
              <Route
                path="transport_documents"
                element={<TransportDocuments />}
              />
              <Route
                path="transport_documents/add"
                element={<AddTransportDocument />}
              />
              <Route
                path="transport_documents/edit/:id"
                element={<EditTransportDocument />}
              />
              <Route
                path="transport_documents/view/:id"
                element={<ViewTransportDocument />}
              />
              <Route
                path="manual_grouped_orders"
                element={<ManualGroupOrder />}
              />
              <Route
                path="manual_grouped_orders/add"
                element={<AddManualGroupOrder />}
              />
              <Route
                path="manual_grouped_orders/view/:id"
                element={<ViewManualGroupOrder />}
              />
              <Route
                path="price_offer_requests"
                element={<PriceOfferRequests />}
              />
              <Route
                path="create_price_offer_request"
                element={<CreatePriceOfferRequests />}
              />
              <Route path="client_offers" element={<ClientOffers />} />
              <Route path="client_offers/add" element={<AddQuotationRequestCustomer />} />
              <Route path="client_offers/edit/:id" element={<EditQuotationRequestCustomer />} />
              <Route path="client_offers/view/:id" element={<ViewQuotationRequestCustomer />} />
              <Route path="quotations_requests" element={<QuotationsRequests />} />
              <Route path="quotations_requests/add" element={<AddQuotationRequest />} />
              <Route path="quotations_requests/view/:id" element={<ViewQuotationRequest />} />
              <Route path="quotations_requests_suppliers" element={<QuotationsRequestsSuppliers />} />
              <Route path="quotations_requests_suppliers/add" element={<AddQuotationRequestSupplier />} />
              <Route path="quotations_requests_suppliers/edit/:id" element={<EditQuotationRequestSupplier />} />
              <Route path="quotations_requests_suppliers/view/:id" element={<ViewQuotationRequestSupplier />} />
              <Route path="provider_offers" element={<ProviderOffers />} />
              <Route path="profits" element={<Profits />} />
              <Route
                path="most_requested_services"
                element={<MostRequestedServices />}
              />
              <Route path="active_accounts" element={<ActiveAccounts />} />
              <Route path="taxes" element={<Taxes />} />
              <Route path="tax_calculation" element={<TaxCalculation />} />
              <Route path="negative_accounts" element={<NegativeAccounts />} />
              <Route path="lowest_cost_price" element={<LowestCostPrice />} />
              <Route
                path="document_reception_reports"
                element={<DocumentReceptionReports />}
              />
              <Route
                path="account_manager_request_reports"
                element={<AccountManagerRequestReports />}
              />
              <Route
                path="operator_trip_reports"
                element={<OperatorTripReports />}
              />
              <Route
                path="trip_request_report"
                element={<TripRequestReport />}
              />
              <Route path="purchase_requests" element={<PurchaseRequests />} />
              <Route
                path="initial_purchase_requests"
                element={<InitialPurchaseRequests />}
              />
              <Route path="maintenance" element={<Maintenance />} />
              <Route path="add_maintenance_request" element={<AddMaintenanceRequest />} />
              <Route path="add_fuel_request" element={<AddFuelRequest />} />
              <Route path="edit_fuel/:id" element={<EditFuelRequest />} />
              <Route path="view_fuel/:id" element={<ViewFuel />} />
              <Route path="fuel" element={<Fuel />} />
              <Route path="vehicle_statement" element={<VehicleStatement />} />
              <Route path="add_entry" element={<AddEntry />} />
              <Route path="edit_entry/:id" element={<EditEntry />} />
              <Route path="view_entry/:id" element={<ViewEntry />} />
              <Route path="vehicle_statement" element={<VehicleStatement />} />
              <Route path="leasing_request/maintenance" element={<MaintenanceTable />} />
              <Route path="add_leasing_maintenance" element={<AddLeasingMaintenance />} />
              <Route path="edit_leasing_maintenance/:id" element={<EditLeasingMaintenance />} />
              <Route path="view_leasing_maintenance/:id" element={<ViewLeasingMaintenance />} />
              <Route path="leasing_request/entries" element={<EntriesTable />} />
              <Route path="leasing_request/entries/add" element={<AddLeasingEntry />} />
              <Route path="leasing_request/entries/edit/:id" element={<EditLeasingEntry />} />
              <Route path="leasing_request/entries/view/:id" element={<ViewLeasingEntry />} />
              <Route path="leasing_request/fuel" element={<FuelTable />} />
              <Route path="leasing_request/fuel/add" element={<AddLeasingFuel />} />
              <Route path="leasing_request/fuel/edit/:id" element={<EditLeasingFuel />} />
              <Route path="leasing_request/fuel/view/:id" element={<ViewLeasingFuel />} />
              <Route path="sales-invoices" element={<SalesInvoices />} />
              <Route path="sales-invoices/show/:id" element={<ShowSalesInvoice />} />
              <Route
                path="sales-invoices/edit/:id/details"
                element={<EditSalesInvoiceDetails />}
              />
              <Route
                path="sales-invoices/edit/:id/items"
                element={<EditSalesInvoiceItems />}
              />
              <Route
                path="sales-invoices/edit/:id"
                element={<CreateSalesInvoices />}
              />
              <Route
                path="sales-invoices/create/collected-travel"
                element={<CreateCollectedTravelSalesInvoice />}
              />
              <Route
                path="sales-invoices/create/collected-rental"
                element={<CreateCollectedRentalSalesInvoice />}
              />
              <Route path="sales-invoices/create/:type" element={<CreateSalesInvoices />} />
              <Route path="credit-notes" element={<CreditNotes />} />
              <Route path="credit-notes/manual" element={<ManualCreditNote />} />
              <Route path="credit-notes/show/:id" element={<ShowCreditNote />} />
              <Route
                path="credit-notes/edit/:id/items"
                element={<EditCreditNoteItems />}
              />
              <Route path="purchase_invoices" element={<PurchaseInvoices />} />
              <Route path="debit-notes" element={<DebitNotes />} />
              <Route path="debit-notes/manual" element={<ManualDebitNote />} />
              <Route path="debit-notes/show/:id" element={<ShowDebitNote />} />
              <Route
                path="debit-notes/edit/:id/items"
                element={<EditDebitNoteItems />}
              />
              <Route path="purchase_invoices/show/:id" element={<ShowPurchaseInvoice />} />
              <Route
                path="purchase_invoices/edit/:id/details"
                element={<EditPurchaseInvoiceDetails />}
              />
              <Route
                path="purchase_invoices/edit/:id/items"
                element={<EditPurchaseInvoiceItems />}
              />
              <Route
                path="purchase_invoices/edit/:id"
                element={<CreatePurchaseInvoices />}
              />
              <Route
                path="purchase_invoices/create/collected-travel"
                element={<CreateCollectedTravelPurchaseInvoice />}
              />
              <Route
                path="purchase_invoices/create/collected-rental"
                element={<CreateCollectedRentalPurchaseInvoice />}
              />
              <Route
                path="purchase_invoices/create/manual"
                element={<CreatePurchaseInvoices />}
              />
              <Route path="vouchers" element={<Vouchers />} />
              <Route path="customer_vouchers" element={<CustomerVouchers />} />
              <Route path="internal_sales" element={<InternalSales />} />
              <Route
                path="internal_purchases"
                element={<InternalPurchases />}
              />
              <Route path="internal_vouchers" element={<InternalVouchers />} />
              <Route path="archive" element={<Archive />} />
              <Route path="commissions" element={<Commissions />} />
              <Route
                path="commission_reports"
                element={<CommissionReports />}
              />
              
              <Route
                path="tripway_partners_list"
                element={<TripwayPartnersList />}
              />
              <Route
                path="add_tripway_partner"
                element={<AddTripwayPartner />}
              />
              <Route
                path="edit_tripway_partner/:id"
                element={<EditTripwayPartner />}
              />
              <Route
                path="show_tripway_partner/:id"
                element={<ShowTripwayPartner />}
              />
              <Route
                path="users_winch_partners"
                element={<UsersWinchPartners />}
              />
              <Route
                path="users_winch_partners/add"
                element={<AddWinchPartnerUser />}
              />
              <Route
                path="users_winch_partners/edit/:winch_partner_user_id"
                element={<EditWinchPartnerUser />}
              />
              <Route
                path="users_winch_partners/view/:id"
                element={<ViewWinchPartnerUser />}
              />

              <Route
                path="clients_partners_winch"
                element={<ClientsPartnersWinch />}
              />
              <Route
                path="partners_contracts"
                element={<PartnersContracts />}
              />
              <Route path="partners_docs" element={<PartnersDocs />} />
              <Route path="docs_carriers" element={<DocsCarriers />} />
              <Route path="vehicles_plural" element={<VehiclesPlural />} />
              <Route path="vehicles_plural/add_vehicle" element={<AddVehicle />} />
              <Route path="vehicles_plural/edit/:id" element={<EditVehicle />} />
              <Route path="vehicles_plural/view/:id" element={<ViewVehicle />} />
              
              <Route path="bank_accounts" element={<BankAccounts />} />
              <Route path="banks" element={<Banks />} />
              <Route
                path="withdrawal_deposit"
                element={<WithdrawalAndDeposit />}
              />
              <Route path="trip_requests" element={<TripRequests />} />
              <Route path="trip_requests/view/:id" element={<ViewTripRequest />} />
              <Route
                path="trip_requests/receive-documents/:id"
                element={<ReceiveTripDocuments />}
              />
              <Route path="trip_requests/edit/:id" element={<EditTripRequest />} />
              <Route path="internal_requests" element={<InternalRequests />} />
              <Route
                path="compensation_approvals"
                element={<CompensationApprovals />}
              />
              <Route
                path="order_issue_reports"
                element={<OrderIssueReports />}
              />
              <Route
                path="order_document_reception"
                element={<OrderDocumentReception />}
              />
              <Route path="documents_files" element={<DocumentsAndFiles />} />
              <Route path="order_summary" element={<OrderSummary />} />
              <Route path="edit_order_details" element={<EditOrderDetails />} />
              <Route path="edit_order_prices" element={<EditOrderPrices />} />
              <Route
                path="edit_shipment_numbers"
                element={<EditShipmentNumbers />}
              />
              <Route path="trip_reports" element={<TripReports />} />
              <Route
                path="duplicate_shipment_numbers"
                element={<DuplicateShipmentNumbers />}
              />
              <Route path="late_entries" element={<LateEntries />} />
              <Route
                path="rental_request_summary"
                element={<RentalRequestSummary />}
              />
              <Route path="rental_reports" element={<RentalReports />} />
              <Route
                path="rental_contracts/add_rental_contract"
                element={<AddRentalContract />}
              />
              <Route
                path="transport_documents"
                element={<TransportDocuments />}
              />
              <Route
                path="transport_documents/add"
                element={<AddTransportDocument />}
              />
              <Route
                path="transport_documents/edit/:id"
                element={<EditTransportDocument />}
              />
              <Route
                path="transport_documents/view/:id"
                element={<ViewTransportDocument />}
              />
              <Route
                path="manual_grouped_orders"
                element={<ManualGroupOrder />}
              />
              <Route
                path="manual_grouped_orders/add"
                element={<AddManualGroupOrder />}
              />
              <Route
                path="manual_grouped_orders/view/:id"
                element={<ViewManualGroupOrder />}
              />
              <Route
                path="price_offer_requests"
                element={<PriceOfferRequests />}
              />
              <Route path="client_offers" element={<ClientOffers />} />
              <Route path="client_offers/add" element={<AddQuotationRequestCustomer />} />
              <Route path="client_offers/edit/:id" element={<EditQuotationRequestCustomer />} />
              <Route path="provider_offers" element={<ProviderOffers />} />
              <Route path="profits" element={<Profits />} />
              <Route
                path="most_requested_services"
                element={<MostRequestedServices />}
              />
              <Route path="active_accounts" element={<ActiveAccounts />} />
              <Route path="taxes" element={<Taxes />} />
              <Route path="tax_calculation" element={<TaxCalculation />} />
              <Route path="negative_accounts" element={<NegativeAccounts />} />
              <Route path="lowest_cost_price" element={<LowestCostPrice />} />
              <Route
                path="document_reception_reports"
                element={<DocumentReceptionReports />}
              />
              <Route
                path="account_manager_request_reports"
                element={<AccountManagerRequestReports />}
              />
              <Route
                path="operator_trip_reports"
                element={<OperatorTripReports />}
              />
              <Route
                path="trip_request_report"
                element={<TripRequestReport />}
              />
              <Route path="fuel" element={<Fuel />} />
              <Route path="vehicle_statement" element={<VehicleStatement />} />
              <Route path="sales_invoices" element={<SalesInvoices />} />
              <Route path="credit-notes" element={<CreditNotes />} />
              <Route path="credit-notes/manual" element={<ManualCreditNote />} />
              <Route path="credit-notes/show/:id" element={<ShowCreditNote />} />
              <Route
                path="credit-notes/edit/:id/items"
                element={<EditCreditNoteItems />}
              />
              <Route path="purchase_invoices" element={<PurchaseInvoices />} />
              <Route path="debit-notes" element={<DebitNotes />} />
              <Route path="debit-notes/manual" element={<ManualDebitNote />} />
              <Route path="debit-notes/show/:id" element={<ShowDebitNote />} />
              <Route
                path="debit-notes/edit/:id/items"
                element={<EditDebitNoteItems />}
              />
              <Route path="purchase_invoices/show/:id" element={<ShowPurchaseInvoice />} />
              <Route
                path="purchase_invoices/edit/:id/details"
                element={<EditPurchaseInvoiceDetails />}
              />
              <Route
                path="purchase_invoices/edit/:id/items"
                element={<EditPurchaseInvoiceItems />}
              />
              <Route
                path="purchase_invoices/edit/:id"
                element={<CreatePurchaseInvoices />}
              />
              <Route
                path="purchase_invoices/create/collected-travel"
                element={<CreateCollectedTravelPurchaseInvoice />}
              />
              <Route
                path="purchase_invoices/create/collected-rental"
                element={<CreateCollectedRentalPurchaseInvoice />}
              />
              <Route
                path="purchase_invoices/create/manual"
                element={<CreatePurchaseInvoices />}
              />
              <Route path="vouchers" element={<Vouchers />} />
              <Route path="customer_vouchers" element={<CustomerVouchers />} />
              <Route path="internal_sales" element={<InternalSales />} />
              <Route
                path="internal_purchases"
                element={<InternalPurchases />}
              />
              <Route path="internal_vouchers" element={<InternalVouchers />} />
              <Route path="archive" element={<Archive />} />
              <Route path="commissions" element={<Commissions />} />
              <Route
                path="commission_reports"
                element={<CommissionReports />}
              />
              <Route
                path="add_purchases_accounts"
                element={<AddPurchasesAccounts />}
              />
              <Route
                path="edit_purchases_accounts/:id"
                element={<EditPurchasesAccounts />}
              />
              <Route
                path="purchases_accounts"
                element={<PurchasesAccounts />}
              />

              <Route
                path="purchase_returns_accounts"
                element={<PurchaseReturnsAccounts />}
              />
              <Route
                path="add_purchase_returns_accounts"
                element={<AddPurchaseReturnsAccounts />}
              />
              <Route
                path="edit_purchase_returns_accounts/:id"
                element={<EditPurchaseReturnsAccounts />}
              />

              <Route
                path="related_party_accounts"
                element={<RelatedPartyAccounts />}
              />
              <Route
                path="add_related_party_accounts"
                element={<AddRelatedPartyAccounts />}
              />
              <Route
                path="edit_related_party_accounts/:id"
                element={<EditRelatedPartyAccounts />}
              />

              <Route
                path="sales_returns_accounts"
                element={<SalesReturnsAccounts />}
              />
              <Route
                path="add_sales_returns_accounts"
                element={<AddSalesReturnsAccounts />}
              />
              <Route
                path="edit_sales_returns_accounts/:id"
                element={<EditSalesReturnsAccounts />}
              />

              <Route path="sales_accounts" element={<SalesAccounts />} />
              <Route path="add_sales_account" element={<AddSalesAccount />} />
              <Route
                path="edit_sales_account/:id"
                element={<EditSalesAccount />}
              />

              <Route path="ratings" element={<Ratings />} />

              <Route path="user_permissions" element={<UserPermissions />} />
              <Route path="add_role" element={<AddRole />} />
              <Route path="edit_role/:id" element={<EditRole />} />

              <Route
                path="job_company_managements"
                element={<JobCompanyManagements />}
              />
              <Route path="job_company_managements/add" element={<AddJob />} />
              <Route
                path="job_company_managements/edit/:id"
                element={<EditJob />}
              />

              <Route path="profile" element={<Profile />} />
              {/* tripway done pages */}
              <Route path="business_sector" element={<BusinessSector />} />
              <Route
                path="business_sector/add"
                element={<AddBusinessSector />}
              />
              <Route
                path="business_sector/edit/:id"
                element={<EditBusinessSector />}
              />
              <Route
                path="business_sector/view/:id"
                element={<ShowBusinessSector />}
              />
              <Route path="employees" element={<Employees />} />
              <Route path="add_employees" element={<AddEmployees />} />
              <Route path="show_employee/:id" element={<ShowEmployee />} />
              <Route path="teamwork-movements" element={<TeamworkMovements />} />
              <Route
                path="teamwork-movements/activities"
                element={<TeamworkMovementsActivities />}
              />
              <Route path="edit_employee/:id" element={<EditEmployee />} />
              <Route path="cities" element={<ShowCities />} />
              <Route path="cities/winch" element={<ShowWinchCities />} />
              <Route path="cities/add_city" element={<AddCities />} />
              <Route path="cities/edit_city/:id" element={<EditCities />} />
              <Route path="countries" element={<ShowCountries />} />
              <Route
                path="countries/edit_country/:id"
                element={<EditCountries />}
              />
              <Route path="countries/add_country" element={<AddCountries />} />
              <Route path="region" element={<ShowRegion />} />
              <Route path="region/edit_region/:id" element={<EditRegion />} />
              <Route path="suppliers" element={<Vendors />} />
              <Route path="suppliers/add_supplier" element={<AddVendor />} />
              <Route
                path="suppliers/edit_supplier/:id"
                element={<EditSupplier />}
              />
              <Route path="faqs" element={<Faqs />} />
              <Route path="add_faqs" element={<AddFAQs />} />
              <Route path="edit_faqs/:id" element={<EditFAQs />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/add_client" element={<AddClient />} />
              <Route path="clients/edit_client/:id" element={<EditClient />} />
              <Route path="clients/show_client/:id" element={<ShowClient />} />
              <Route path="leads" element={<LeadsCustomer />} />
              <Route path="leads/add" element={<AddLead />} />
              <Route path="leads/edit/:id" element={<EditLead />} />
              <Route path="leads/view/:id" element={<ShowLead />} />
              <Route path="leads/:id/activities" element={<LeadActivities />} />
              <Route path="target_customers" element={<TargetCustomer />} />
              <Route
                path="target_customers/view/:id"
                element={<ShowTargetCustomer />}
              />

              <Route path="file_archive_types" element={<FileArchiveTypes />} />
              <Route
                path="add_file_archive_types"
                element={<AddFileArchiveTypes />}
              />
              <Route
                path="edit_file_archive_types/:id"
                element={<EditFileArchiveTypes />}
              />

              <Route path="order_issue_types" element={<OrderIssueTypes />} />
              <Route
                path="add_order_issue_types"
                element={<AddOrderIssueTypes />}
              />
              <Route
                path="edit_order_issue_types/:id"
                element={<EditOrderIssueTypes />}
              />
              <Route path="services_types" element={<ServicesTypes />} />
              <Route
                path="add_services_types"
                element={<AddServicesTypes />}
              />
              <Route
                path="edit_services_types/:id"
                element={<EditServicesTypes />}
              />
              <Route
                path="show_services_types/:id"
                element={<ShowServicesTypes />}
              />
              <Route path="quotation_features" element={<QuotationFeatures />} />
              <Route
                path="add_quotation_features"
                element={<AddQuotationFeatures />}
              />
              <Route
                path="edit_quotation_features/:id"
                element={<EditQuotationFeatures />}
              />
              <Route path="load_types" element={<LoadTypes />} />
              <Route
                path="add_load_types"
                element={<AddLoadTypes />}
              />
              <Route
                path="edit_load_types/:id"
                element={<EditLoadTypes />}
              />
              <Route path="good_types" element={<GoodTypes />} />
              <Route
                path="add_good_types"
                element={<AddGoodTypes />}
              />
              <Route
                path="edit_good_types/:id"
                element={<EditGoodTypes />}
              />
              <Route path="car_departments" element={<CarDepartments />} />
              <Route
                path="add_car_department"
                element={<AddCarDepartments />}
              />
              <Route
                path="edit_car_department/:id"
                element={<EditCarDepartments />}
              />
              
              <Route
                path="main_settings"
                element={<MainSettings />}
              />
              <Route path="furniture_questions" element={<FurnitureQuestions />} />
              <Route
                path="add_furniture_questions"
                element={<AddFurnitureQuestions />}
              />
              <Route
                path="edit_furniture_questions/:id"
                element={<EditFurnitureQuestions />}
              />
              <Route path="document_type" element={<DocumentType />} />
              <Route path="add_document_type" element={<AddDocumentType />} />
              <Route
                path="edit_document_type/:id"
                element={<EditDocumentType />}
              />
              <Route
                path="category_rush_hours"
                element={<CategoryRushHours />}
              />
              <Route
                path="edit_category_rush_hours/:id"
                element={<EditCategoryRushHours />}
              />
               <Route
                path="fleet_management_types"
                element={<FleetManagementTypes />}
              />
              <Route
                path="add_fleet_management_type"
                element={<AddFleetRequest />}
              />
              <Route
                path="edit_fleet_management_type/:id"
                element={<EditFleetRequest />}
              />
              <Route
                path="office_document_type"
                element={<OfficeDocumentType />}
              />
              <Route
                path="office_document_type/add"
                element={<AddOfficeDocType />}
              />
              <Route
                path="office_document_type/edit/:id"
                element={<EditOfficeDocType />}
              />
               <Route
                path="service_categories"
                element={<Service_categories />}
              />
              <Route
                path="services_categories/create"
                element={<AddServiceCategories />}
              />
              <Route
                path="services_categories/edit/:id"
                element={<EditServiceCategories />}
              />
              <Route
                path="compensation_categories"
                element={<Compensation_categories />}
              />
              <Route
                path="compensation_categories/create"
                element={<Addcompensation_categories />}
              />
              <Route
                path="compensation_categories/edit/:id"
                element={<Editcompensation_categories />}
              />
              
              <Route path="vehicle_companies" element={<VehicleCompanies />} />
              <Route
                path="add_vehicle_companies"
                element={<AddVehicleCompanies />}
              />
              <Route
                path="edit_vehicle_companies/:id"
                element={<EditVehicleCompanies />}
              />

              <Route
                path="company_managements"
                element={<CompanyManagements />}
              />
              <Route
                path="company_managements/add_management"
                element={<AddManagement />}
              />
              <Route
                path="company_managements/edit_managemnet/:id"
                element={<EditManagement />}
              />
              <Route path="edit_GoogleMapLinks" element={<GoogleMapLinks />} />

              <Route path="Positions" element={<ShowPositions />} />
              <Route path="Positions/add_position" element={<AddPositions />} />
              <Route
                path="Positions/edit_position/:id"
                element={<EditPositions />}
              />

              <Route path="AttachmentsType" element={<AttachmentsType />} />
              <Route
                path="AttachmentsType/add_attachment"
                element={<AddAttachments />}
              />
              <Route
                path="AttachmentsType/edit_attachment/:id"
                element={<EditAttachments />}
              />
              <Route path="Offices" element={<ShowOffices />} />
              <Route path="Offices/add_office" element={<AddOffices />} />
              <Route path="Offices/edit_office/:id" element={<EditOffices />} />
              <Route path="chart_of_accounts" element={<ChartOfAccounts />} />
              <Route path="add_chart_accounts" element={<AddChartOfAccounts />} />
              <Route path="edit_chart_accounts/:id" element={<EditChartOfAccounts />} />
              <Route path="show_chart_accounts/:id" element={<ShowChartOfAccounts />} />
              <Route path="easy_entries" element={<EasyEntries />} />
              <Route path="add_easy_entries" element={<AddEasyEntries />} />
              <Route path="edit_easy_entries/:id" element={<EditEasyEntries />} />
              <Route path="manual_entries" element={<ManualEntries />} />
              <Route path="manual_entries/add" element={<AddManualEntry />} />
              <Route path="manual_entries/edit/:id" element={<EditManualEntry />} />
              <Route path="manual_entries/view/:id" element={<ViewManualEntry />} />
              <Route path="customer_balances" element={<CustomerBalances />} />
              <Route path="account_statement" element={<AccountStatment />} />
              <Route path="general_ledger" element={<Generalledger />} />
              <Route path="journal_book" element={<JournalBook />} />
              <Route path="trial_balance" element={<TrialBalance />} />
              <Route path="customer_dues" element={<CustomerDue />} />
              <Route path="income_statement" element={<IncomeStatement />} />
              <Route path="balance_sheet" element={<BalanceSheet />} />
              <Route path="tax_invoice_reports" element={<TaxInvoices />} />
              <Route path="customer_profit" element={<CustomerProfit />} />
              <Route path="tripway_branches" element={<Branches />} />
              <Route path="add_tripway_branch" element={<AddBranch />} />
              <Route path="edit_tripway_branch/:id" element={<EditBranch />} />
              <Route path="show_tripway_branch/:id" element={<ViewBranch />} />
              {/* new tripway routes */}
              <Route path="roles" element={<Roles />} />
              <Route path="add_user_role" element={<AddUserRole />} />
              <Route path="edit_user_role/:id" element={<EditUserRole />} />
              <Route path="show_user_role/:id" element={<ShowUserRole />} />
              <Route path="permissions" element={<Permissions />} />
              <Route path="add_permission" element={<AddPermission />} />
              <Route path="edit_permission/:id" element={<EditPermission />} />
              <Route
                path="business_sector/users"
                element={<BusinessSectorUsers />}
              />
              <Route
                path="business-sector-users/add"
                element={<AddBusinessSectorUser />}
              />
              <Route
                path="business-sector-users/:id"
                element={<ShowBusinessSectorUser />}
              />
              <Route
                path="business-sector-users/edit/:id"
                element={<EditBusinessSectorUser />}
              />
              <Route
                path="business_sector/official_recieving_docs"
                element={<OfficialRecievingDocs />}
              />
              <Route
                path="business-sector-responsibles/add"
                element={<AddBusinessSectorResponsible />}
              />
              <Route
                path="business_sector/contracts"
                element={<BusinessSectorContracts />}
              />
              <Route
                path="business-sector-contracts/add"
                element={<AddBusinessSectorContract />}
              />
              <Route
                path="business-sector-contracts/add-electronic"
                element={<ElectronicContractSetup />}
              />
              <Route
                path="business-sector-contracts/edit-electronic/:id"
                element={<ElectronicContractSetup />}
              />
              <Route
                path="business-sector-contracts/copy-electronic/:id"
                element={<ElectronicContractSetup />}
              />
              <Route
                path="business-sector-contracts/add-electronic/editor"
                element={<AddElectronicContract />}
              />
              <Route
                path="business-sector-contracts/preview-electronic/:id"
                element={<ElectronicContractPreviewPage />}
              />
              <Route
                path="business-sector-contracts/edit/:id"
                element={<EditBusinessSectorContract />}
              />
              <Route
                path="business_sector/credit_limit"
                element={<CreditLimit />}
              />
              <Route
                path="business_sector/business_docs"
                element={<BusinessDocs />}
              />
              <Route
                path="business-sector-documents/add"
                element={<AddBusinessSectorDocument />}
              />
              <Route path="favourites" element={<Favourites />} />
              <Route
                path="exceeded_commission_accounts"
                element={<ExceededCommissionAccounts />}
              />
              <Route path="export_orders" element={<ExportOrders />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="add_driver" element={<AddDriver />} />
              <Route path="edit_driver/:id" element={<EditDriver />} />
              <Route path="show_driver/:id" element={<ShowDriver />} />
              <Route path="establishment_documents" element={<EstablishmentDocuments />} />
              <Route path="establishment_documents/add" element={<AddEstablishmentDocuments />} />
              <Route path="establishment_documents/show/:id" element={<ShowEstablishmentDocuments />} />
              <Route path="employee_document" element={<EmployeeDocument />} />
              <Route path="employee_document/add" element={<AddEmployeeDocument />} />
              <Route path="employee_document/show/:id" element={<ShowEmployeeDocument />} />
              <Route path="vehicle_document" element={<VehicleDocument />} />
              <Route path="vehicle_document/add" element={<AddVehicleDocument />} />
              <Route path="vehicle_document/show/:id" element={<ShowVehicleDocument />} />
              <Route path="driver_documents" element={<DriverDocument />} />
              <Route path="driver_documents/add" element={<AddDriverDocument />} />
              <Route path="driver_documents/show/:id" element={<ShowDriverDocument />} />
              <Route path="fleet_mandates" element={<FleetMandates />} />
              <Route path="fleet_mandates/add" element={<AddFleetMandates />} />
              <Route path="cost_center_accounts" element={<CostCenterAccounts />} />
              <Route path="cost_center_accounts/add" element={<AddCostCenterAccount />} />
              <Route path="bonds" element={<Bonds />} />
              <Route path="bonds/add" element={<AddBond />} />
              <Route path="bonds/show/:id" element={<ShowBond />} />
              <Route path="bonds/edit/:id" element={<EditBond />} />
            </Route>
            {/* <Route path="register" element={<Register />} /> */}
            <Route path="login" element={<Login />} />
            <Route path="verify_code" element={<VerifyCode />} />
            <Route path="reset_password" element={<ResetPassword />} />
            {/* catch all route for unknown paths */}
            <Route path="*" element={<NotFound />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
          </Routes>
          </Suspense>
          <Footer />
        </Router>
      </TitleProvider>
    </>
  );
}

export default App;
